import {useIntl} from 'react-intl';
import {Box, BoxProps, Center, Avatar, Text, Flex} from '@chakra-ui/react';
import {AddIcon} from '@chakra-ui/icons';

import {useTranslate} from '@eksab/i18n';
import {ArrowDownIcon, TShirtIcon} from '@eksab/assets/icons';

import {CaptainIndicator} from '../CaptainIndicator';
import type {Player} from '../../types';

type PlayerSpotProps = {
  player: Player | null;
  selected?: boolean;
  showPoints?: boolean;
  onPlayerChange?: () => void;
  onPlayerInfoClick?: (player: Player) => void;
  inField?: boolean;
  isLineupEditable?: boolean;
} & BoxProps;

export const PlayerSpot = ({
  player,
  inField = true,
  selected,
  onPlayerChange,
  onPlayerInfoClick,
  isLineupEditable,
  showPoints = true,
  ...props
}: PlayerSpotProps) => {
  const format = useIntl().formatNumber;

  const t = useTranslate();

  return (
    <Flex
      className="player"
      data-selected={selected}
      flex={1}
      pos="relative"
      flexDir="column"
      justify="space-between"
      align="center"
      gap="2%"
      w="10"
      maxW="30%"
      {...props}
    >
      <Avatar
        className="player-avatar"
        pos="relative"
        bg="white"
        p="1.5"
        w="auto"
        h="55%"
        shadow="xs"
        sx={{aspectRatio: '1/1'}}
        borderWidth={selected ? '2px' : '0px'}
        borderColor="black"
        data-selected={selected}
        src={player?.image?.thumb ?? player?.image?.original}
        icon={<TShirtIcon filled={false} width="80%" height="auto" />}
        cursor={onPlayerChange ? 'pointer' : 'initial'}
        onClick={onPlayerChange}
      >
        {!player && isLineupEditable && (
          <Center
            rounded="full"
            bg="green.400"
            p="0.5"
            w="30%"
            pos="absolute"
            insetEnd="20%"
            bottom="15%"
            sx={{aspectRatio: '1/1'}}
          >
            <AddIcon flex={1} w="full" h="auto" color="white" />
          </Center>
        )}

        {(!!player?.is_captain || !!player?.is_vice_captain) && (
          <CaptainIndicator status={player.is_captain ? 'captain' : 'vice-captain'} />
        )}
      </Avatar>

      <Flex
        className="player-info"
        bg={inField ? (!player ? 'white' : selected ? 'black' : 'green.500') : 'white'}
        flex={1}
        maxH="40%"
        align="center"
        justify="center"
        gap="0.5"
        w="full"
        minH="7"
        p="0.5"
        fontSize={['max(2vw, 0.55rem)', null, 'min(1.5vw, 0.75rem)']}
        lineHeight="1.2"
        fontWeight="700"
        textAlign="center"
        rounded="0.25rem"
      >
        {player ? (
          <PlayerInfo player={player} onClick={onPlayerInfoClick} substitute={!inField} />
        ) : (
          <Text fontWeight="extrabold" color="green.500" userSelect="none">
            {isLineupEditable ? t('app.actions.add') : '—'}
          </Text>
        )}

        {player?.scored_fantasy_points !== undefined && (
          <Box
            bg={inField ? 'gray.600' : 'white'}
            pos="absolute"
            top="35%"
            insetStart="0"
            rounded="md"
            shadow="xs"
            minW="4ch"
            maxW="6ch"
            textAlign="center"
            textOverflow="clip"
          >
            <Text px="0.5" fontSize="clamp(0.5rem, 2.5vw, 0.75rem)" userSelect="none">
              {format(player?.scored_fantasy_points)}
            </Text>
          </Box>
        )}

        {player?.is_substituted && (
          <Box pos="absolute" top="35%" insetEnd={[0, 2]}>
            <ArrowDownIcon scale={!player.is_substitute ? 1 : -1} transform="auto" />
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

type PlayerInfoProps = {
  player: Player;
  onClick: ((player: Player) => void) | undefined;
  substitute?: boolean;
};

const PlayerInfo = ({player, substitute, onClick}: PlayerInfoProps) => (
  <>
    <Center
      as="p"
      dir="auto"
      title={player.common_name}
      className="player-info-name"
      noOfLines={{base: 2, xl: 3}}
      w="full"
      maxH="full"
      fontSize="inherit"
      fontWeight="700"
      color={substitute ? 'black' : 'white'}
      sx={{hyphens: 'auto'}}
      cursor={onClick ? 'pointer' : 'initial'}
      onClick={() => onClick?.(player)}
    >
      {player.common_name}
    </Center>
  </>
);
